/**
 * emailDeliveryDashboardApi
 *
 * EMAIL-DELIVERY-TRACKING-004A: client for the real, read-only EDT
 * Dashboard/War Room summary API (`GET /api/email-delivery/dashboard/summary`).
 *
 * Wraps the SAME shared `api` axios instance every other real-backed War
 * Room surface already uses (see lib/api.js, emailHygieneReconciliationApi.js)
 * — no second HTTP stack. Auth (Bearer token) and tenant scoping
 * (X-Account-ID header) are attached automatically by that instance's own
 * request interceptor; this module never accepts or forwards a caller-
 * supplied tenant id.
 *
 * DASH-EMAIL-019: the backend now accepts two optional MESSAGE-grain
 * filters, `broadcast_id` (governed BRC#### shape) and `state` (any
 * recognized US state name/abbreviation or DC -- normalized server-side,
 * see email_delivery_geography.py).
 *
 * DASH-EMAIL-P1-IMPL-032 (supersedes the prior "no Program/Campaign
 * parameter" note): Program/Campaign (and Audience) now resolve through
 * the tenant-scoped governed Broadcast catalog server-side (DASH-EMAIL-
 * P1-IMPL-022) rather than needing a persisted MESSAGE field of their
 * own -- see `audienceId`/`campaignId`/`programId` below. Creative-Version
 * remains unsupported (no Dashboard filter for it exists on either real
 * endpoint yet).
 *
 * An invalid broadcastId/audienceId/campaignId/programId/state value is
 * rejected by the backend with a 4xx -- this client does not pre-validate,
 * it simply forwards whatever the caller supplies (already-normalized/
 * selected values, per useEmailDashboardData.js's own contract).
 *
 * DASH-EMAIL-022: a third optional MESSAGE-grain filter,
 * `observedGhlEnvironment` (serialized as `observed_ghl_environment`),
 * mirrors the exact same conditional-inclusion convention as
 * broadcastId/state above -- included only when a real, non-"all" value
 * is supplied. This is the already-persisted, immutable GHL
 * discovery-source provenance field (DASH-EMAIL-021 characterization) --
 * it proves discovery provenance ONLY, never SendGrid ingestion, delivery
 * success, Broadcast execution environment, or any reconciliation between
 * concepts. Deliberately unrelated to the shared, mock-catalog-driven
 * `environment` prop EmailFilterBar.jsx/emailDashboardMockAdapter.js
 * already use elsewhere in this same Dashboard -- that prop must never be
 * forwarded here (see useEmailDashboardData.js's own contract for why).
 *
 * DASH-EMAIL-P1-IMPL-004: `period`/`startDate`/`endDate` -- the SAME
 * reporting-period contract emailDeliveryLpeFunnelApi.js's
 * getEmailDeliveryLpeFunnelSummary uses, resolved server-side by the SAME
 * shared boundary resolver. As of this checkpoint, this now scopes
 * `metrics.delivered` ONLY -- every other field in `metrics` remains its
 * pre-existing all-time semantics (see the backend's own docstring for
 * exactly why). `period` defaults to "mtd" server-side if omitted, but
 * this client always forwards the caller's own selected value (see
 * useEmailDashboardData.js's own contract).
 *
 * `metrics` is COUNTS ONLY. `availability` documents, per derived-metric
 * key (e.g. "delivery_rate", "bounce_rate", and now "delivered"'s own
 * historical-coverage disclosure), why/how that value is scoped — never
 * fabricate a rate from these counts on the frontend either.
 *
 * DASH-EMAIL-P1-IMPL-032: three more optional MESSAGE-grain-adjacent
 * governed filters, `audienceId`/`campaignId`/`programId` (serialized as
 * `audience_id`/`campaign_id`/`program_id`), added by DASH-EMAIL-P1-
 * IMPL-022 on the backend — each resolves through the tenant-scoped
 * governed Broadcast catalog to a `broadcast_id` constraint server-side,
 * never a field on EmailDeliveryMessage itself. Same conditional-inclusion
 * convention as broadcastId/state/observedGhlEnvironment: omitted entirely
 * when falsy/"all", never sent as an empty string. Values must always be
 * the stable governed operational ID (AUD###/PRG###/CMP####) — never a
 * mutable display name.
 */
import { api } from "./api";

export async function getEmailDeliveryDashboardSummary({
  period,
  startDate,
  endDate,
  broadcastId,
  audienceId,
  campaignId,
  programId,
  state,
  observedGhlEnvironment,
} = {}) {
  const params = {};
  if (period) params.period = period;
  if (period === "custom") {
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
  }
  if (broadcastId) params.broadcast_id = broadcastId;
  if (audienceId) params.audience_id = audienceId;
  if (campaignId) params.campaign_id = campaignId;
  if (programId) params.program_id = programId;
  if (state) params.state = state;
  if (observedGhlEnvironment && observedGhlEnvironment !== "all") {
    params.observed_ghl_environment = observedGhlEnvironment;
  }
  const res = await api.get("/email-delivery/dashboard/summary", { params });
  return res.data;
}

export default getEmailDeliveryDashboardSummary;
